import React, { Component, Fragment } from 'react';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import AffirmBnplBtn from './Buttons/AffirmBnplButton/AffirmBnplBtn';
import { PbbContinueButton } from './Buttons/PbbContinueButton/PbbContinueButton';

import { isPayPalSetOnTheWindow } from 'businessLogic/Payment/Paypal';
import PayPalAppConnectButton from 'components/Core/Payment/Buttons/AppConnectButton/PayPalAppConnectButton';
import PayButtonWithConsent from 'components/Core/Payment/Buttons/PayButton/PayButtonWithConsent';
import PaypalPayBtn from 'components/Core/Payment/Buttons/PayPalPayBtn/PaypalPayBtn';
import { checkPayPalCheckoutEnabled } from 'components/Core/Payment/Growth/PayPalCheckoutExperiment/utils/checkPayPalCheckoutEnabled';
import PaymentForm from 'components/Core/Payment/PaymentForm/PaymentForm';
import PayOverTimeCard from 'components/Core/Payment/PaymentSection/PayOverTimeCard';
import AffirmBnplDisclaimer from 'components/Shared/AffirmBnplDisclaimer/AffirmBnplDisclaimer';
import SpinnerCentered from 'components/Shared/Spinner/SpinnerCentered/SpinnerCentered';
import { IxpExperiment } from 'reporting/SegmentIO';
import { amountToCents } from 'shared/utils';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { insightSelectors } from 'store/insight/selectors';
import { isInEnhancementsHoldoutExperiment } from 'store/ixp/selector';
import { nanopaySelectors } from 'store/nanopay/selectors';
import {
  onPayButtonClicked,
  onContinuePbbButtonClick,
  onAffirmBnplButtonClick,
} from 'store/payment/slice';
import { saleSelectors, isForcedAutopayRecurringSelector } from 'store/sale/selectors';
import { RootState } from 'store/store';
import { fetchWallets } from 'store/wallet/slice';
import { colors } from 'styles/cp';
import { Auth } from 'types/Auth';
import { Customizations } from 'types/CompanyInfo';
import { Config } from 'types/Config';
import { FeatureFlags } from 'types/FeatureFlags';
import { IXP } from 'types/IXP';
import { Insight } from 'types/Insight';
import { Nanopay } from 'types/Nanopay';
import { Payment as PaymentTypes } from 'types/Payment';
import { Wallet } from 'types/Wallet';

type PaymentProps = {
  currency: string;
  auth: Pick<Auth, 'isUserSignedIn'>;
  featureFlags: FeatureFlags;
  payment: Pick<
    PaymentTypes,
    | 'isPayPalCommerceInvoice'
    | 'paymentMethodType'
    | 'paymentError'
    | 'isAmountValid'
    | 'amount'
    | 'paymentStatus'
    | 'payPalProcessor'
    | 'selectedBank'
  >;
  wallet: Pick<
    Wallet,
    | 'userWallets'
    | 'isDetectingApplePayStatus'
    | 'isDetectingPayPalStatus'
    | 'fetchWalletStatus'
    | 'cardOnFile'
  >;
  customizations: Customizations;
  dueDate: string;
  ixp: { [key: string]: IxpExperiment };
  shouldShowPayButton?: boolean;
  fetchWallets: Function;
  recurringInfo: Record<string, any>;
  isSubscription: boolean;
  insight: Pick<Insight, 'isPreview'>;
  config: Config;
  onPayButtonClicked: () => void;
  onContinuePbbButtonClick: () => void;
  onAffirmBnplButtonClick: () => void;
  subscriptionInfo: Record<string, any>;
  nanopay: Pick<Nanopay, 'hasNanopayConnected' | 'payingWithAccount'>;
  isPayEnabled: boolean;
  paymentMethodsOrder: any[];
  sale: any;
  achOnlineConvenienceFeeEnabled: boolean;
  isForcedAutoPay: boolean;
  isInEnhancementsHoldoutExperiment?: boolean;
};

class Payment extends Component<PaymentProps, {}> {
  constructor(props: PaymentProps) {
    super(props);
  }
  componentDidMount() {
    const { auth, wallet, fetchWallets } = this.props;
    if (auth.isUserSignedIn && !wallet.userWallets) {
      fetchWallets();
    }
  }

  render() {
    const {
      dueDate,
      wallet: { isDetectingApplePayStatus, isDetectingPayPalStatus, fetchWalletStatus, cardOnFile },
      payment: { isPayPalCommerceInvoice, paymentMethodType, selectedBank },
      auth,
      recurringInfo,
      isSubscription,
      shouldShowPayButton = true,
      insight: { isPreview } = {},
      isPayEnabled,
      ixp,
      featureFlags,
      paymentMethodsOrder,
      sale,
      achOnlineConvenienceFeeEnabled,
    } = this.props;
    const isFetchingWallet =
      fetchWalletStatus === 'fetching' || (!fetchWalletStatus && auth.isUserSignedIn);

    if (!isPreview && (isDetectingApplePayStatus || isDetectingPayPalStatus || isFetchingWallet)) {
      return <SpinnerCentered />;
    }

    const { isPPCheckoutExperiment, isPPExperimentTreatmentA, isPPExperimentTreatmentB } =
      checkPayPalCheckoutEnabled(
        isPayEnabled,
        ixp as IXP,
        featureFlags,
        paymentMethodsOrder,
        sale,
        achOnlineConvenienceFeeEnabled
      );

    const renderPaymentButton = () => {
      if (paymentMethodType === 'paypal_ppaam' || paymentMethodType === 'venmo') {
        if (!isPayPalSetOnTheWindow(paymentMethodType) || isPPCheckoutExperiment) {
          return <></>;
        }
        return (
          // @ts-ignore
          <PaypalPayBtn isMobileFixedPositionDisabled={cardOnFile ? true : false} />
        );
      }
      if (paymentMethodType === 'pbb') {
        return (
          <PbbContinueButton
            dueDate={dueDate}
            currency={this.props.currency}
            payment={this.props.payment}
            selectedBank={selectedBank}
            onContinuePbbButtonClick={this.props.onContinuePbbButtonClick}
          />
        );
      }
      if (paymentMethodType === 'affirm_bnpl') {
        return (
          <>
            <AffirmBnplBtn
              payment={this.props.payment}
              onAffirmBnplButtonClick={this.props.onAffirmBnplButtonClick}
            />
            <AffirmBnplDisclaimer />
          </>
        );
      }
      if (isPayPalCommerceInvoice && paymentMethodType === 'pp') {
        if (!isPayPalSetOnTheWindow(paymentMethodType)) {
          return <></>;
        }
        return (
          <PayPalAppConnectButton
            // @ts-ignore
            config={this.props.config}
            wallet={this.props.wallet}
            auth={this.props.auth}
            payment={this.props.payment}
            featureFlags={this.props.featureFlags}
          />
        );
      }
      if (paymentMethodType === 'nanopay') {
        const { nanopay } = this.props;
        if (nanopay) {
          const { hasNanopayConnected, payingWithAccount } = nanopay;
          if (!hasNanopayConnected || !payingWithAccount || !payingWithAccount.id) {
            return null;
          }
        } else {
          return null;
        }
      }
      if (!isPayPalCommerceInvoice) {
        return (
          <PayButtonWithConsent
            customizations={this.props.customizations}
            currency={this.props.currency}
            config={this.props.config}
            wallet={this.props.wallet}
            auth={this.props.auth}
            payment={this.props.payment}
            featureFlags={this.props.featureFlags}
            onPayButtonClicked={this.props.onPayButtonClicked}
            recurringInfo={recurringInfo}
            dueDate={dueDate}
            isSubscription={isSubscription}
            subscriptionInfo={this.props.subscriptionInfo}
            ixp={this.props.ixp}
            isPPCheckoutExperiment={isPPCheckoutExperiment}
            isForcedAutoPay={this.props.isForcedAutoPay}
            isInEnhancementsHoldoutExperiment={this.props.isInEnhancementsHoldoutExperiment}
          />
        );
      }
      return null;
    };
    return (
      <Fragment>
        <PaymentForm
          isPPCheckoutExperiment={isPPCheckoutExperiment}
          isPPExperimentTreatmentA={isPPExperimentTreatmentA}
          isPPExperimentTreatmentB={isPPExperimentTreatmentB}
        />
        <PayOverTimeCard
          amountCents={amountToCents(this.props.payment.amount)}
          isActive={paymentMethodType === 'affirm_bnpl'}
        />
        {shouldShowPayButton && renderPaymentButton()}
        <style jsx>{`
          .bugs {
            ul {
              list-style: none;
              padding: 10px 0;
              margin: 0;
              text-align: center;

              :global(li) {
                display: inline-block;

                &:not(first-child) {
                  margin-left: 5px;
                }
              }
            }
          }
          .or-payment {
            display: flex;
            color: ${colors.gray02};
            justify-content: space-around;
            align-items: center;
            margin: 12px 0;
          }
          .ApplePay {
            margin-bottom: 12px;
          }
        `}</style>
      </Fragment>
    );
  }
}

function mapStateToProps(store: RootState) {
  const { sale, payment, insight, config, nanopay, wallet, auth, ixp, featureFlags, companyInfo } =
    store;
  return {
    customizations: companyInfoSelectors.customizationsSelector(companyInfo),
    currency: saleSelectors.currencySelector(sale),
    dueDate: saleSelectors.dueDateSelector(sale),
    auth,
    payment,
    nanopay,
    wallet,
    featureFlags,
    ixp,
    insight,
    config,
    sale,
    isCADAndPaymentMethodNanopay: nanopaySelectors.isCADAndPaymentMethodNanopaySelector(
      saleSelectors.currencySelector(sale),
      payment.paymentMethodType
    ),
    recurringInfo: saleSelectors.getRecurringInfo(sale),
    isSubscription: saleSelectors.isSubscription(sale),
    subscriptionInfo: saleSelectors.subscriptionInfo(sale),
    isPayEnabled: insightSelectors.isPayEnabledSelector(insight),
    paymentMethodsOrder: wallet.enabledPaymentMethods,
    achOnlineConvenienceFeeEnabled: saleSelectors.achOnlineConvenienceFeeEnabledSelector(sale),
    isForcedAutoPay: isForcedAutopayRecurringSelector(store),
    isInEnhancementsHoldoutExperiment: isInEnhancementsHoldoutExperiment(store),
  };
}

export default injectIntl(
  // @ts-ignore
  connect(mapStateToProps, {
    onPayButtonClicked,
    onContinuePbbButtonClick,
    onAffirmBnplButtonClick,
    fetchWallets,
    // @ts-ignore
  })(Payment)
);
